// ============================================
// LIGA AJUPAM - App Analytics (PWA participantes)
// ============================================
// Virtual pageviews + tags de contexto + eventos de acción para la PWA de
// participantes. La navegación interna es por estado React (`setView`), NO por
// rutas, así que sin esto GA4/Clarity ven casi todo el tráfico bajo "/" (~92%):
// dashboard, tabla, fixture, agendar, mi-equipo y perfil quedan indistinguibles.
//
// Espeja utils/publicAnalytics.ts (mismo `declare global`, mismo try/catch
// silencioso). Namespace de path: "/app/<pantalla>" para no chocar con las
// rutas públicas reales (/liga/..., /join/..., /suplencia/...).

import type PostHogDefault from 'posthog-js';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
  }
}

// PostHog se carga de forma DIFERIDA (dynamic import) para no arrastrar ~70KB gz
// al bundle inicial / critical path. El init real vive en index.tsx (también
// diferido). Hasta que el SDK esté importado + inicializado, ph() devuelve null y
// los calls quedan no-op — misma semántica que el chequeo `__loaded` previo
// (dev / sin key / init pendiente). posthog-js es un singleton global: este
// import() y el de index.tsx resuelven a la misma instancia.
type PostHogInstance = typeof PostHogDefault;
let phInstance: PostHogInstance | null = null;
let phRequested = false;
const ph = (): PostHogInstance | null => {
  if (!phInstance && !phRequested && import.meta.env.PROD) {
    phRequested = true;
    import('posthog-js').then((m) => { phInstance = m.default; }).catch(() => {});
  }
  return phInstance && (phInstance as any).__loaded ? phInstance : null;
};

// Mapa view (estado React de App.tsx) → path legible para GA4.
// Las views no listadas caen a `/app/<view>`.
const VIEW_PATHS: Record<string, string> = {
  dashboard: '/app/inicio',
  standings: '/app/tabla',
  schedule: '/app/partidos',
  'schedule-agenda': '/app/agenda',
  scheduling: '/app/agendar-partido',
  team: '/app/mi-equipo',
  profile: '/app/perfil',
  convenios: '/app/convenios',
  simulacro: '/app/simulacro',
  'playoffs-bracket': '/app/playoffs',
  registration: '/app/inscripcion',
  substitute_registration: '/app/suplencia',
  public_matches: '/app/partidos-publicos',
};

function viewToPath(view: string): string {
  return VIEW_PATHS[view] ?? `/app/${view}`;
}

/**
 * Virtual pageview de una pantalla interna de participante.
 * Llamar cada vez que cambia `view` (con usuario logueado).
 */
export function trackAppView(view: string) {
  try {
    const path = viewToPath(view);
    window.gtag?.('event', 'page_view', {
      page_path: path,
      page_title: `App · ${view}`,
      page_location: window.location.origin + path,
    });
    // Clarity agrupa heatmaps/recordings por tag → permite filtrar por pantalla.
    window.clarity?.('set', 'app_view', view);
    window.clarity?.('set', 'view_path', path);
    ph()?.capture('$pageview', { $current_url: window.location.origin + path, app_view: view });
  } catch {
    // Nunca romper la app por analytics
  }
}

/**
 * Virtual pageview de un tab del dashboard ADMIN. Namespace "/admin/<tab>" para
 * no chocar con "/app/..." (participantes) ni con las rutas publicas. El admin
 * tambien navega por estado (?tab=), asi que sin esto GA4/Clarity ven todo bajo
 * "/" y no se puede medir que secciones del admin se usan (insumo para validar
 * la reorg del menu con datos reales). v6.11.10
 */
export function trackAdminView(tab: string) {
  try {
    const path = `/admin/${tab}`;
    window.gtag?.('event', 'page_view', {
      page_path: path,
      page_title: `Admin · ${tab}`,
      page_location: window.location.origin + path,
    });
    window.clarity?.('set', 'admin_tab', tab);
    window.clarity?.('set', 'view_path', path);
    ph()?.capture('$pageview', { $current_url: window.location.origin + path, admin_tab: tab });
  } catch {
    // Nunca romper el panel por analytics
  }
}

// Eventos de acción del participante. Hoy GA4 registra 0 de estos: toda la
// instrumentación custom existente es del lado público/fans. El sufijo
// `_submit` marca intento y `_success` marca completado, para medir abandono
// (ej: `form_start` sin completado en el embudo de inscripción/carga).
export type AppEvent =
  | 'match_result_submit'    // intento de cargar resultado
  | 'match_result_success'   // resultado cargado OK
  | 'match_propose'          // intento de proponer partido
  | 'match_propose_success'  // propuesta enviada OK
  // v6.11.154 — reactivar una propuesta que se venció sin confirmarse. Va
  // aparte de `match_propose` a propósito: mide si la tarjeta nueva rescata
  // partidos que hoy se pierden, y eso no se ve mezclado con las propuestas
  // que se arman desde cero.
  | 'match_proposal_reactivate'
  | 'match_proposal_reactivate_success'
  | 'challenge_send'         // desafío enviado
  | 'challenge_fix'          // retado fijó día/hora/cancha del desafío (v6.11.8)
  | 'availability_set'       // disponibilidad guardada
  | 'registration_step'      // avance de paso en inscripción
  | 'registration_success'   // inscripción completada
  | 'profile_update'         // perfil editado
  | 'hint_open'              // se abrió un InfoHint contextual
  | 'help_open'              // se abrió el HelpDrawer
  | 'assistant_open'         // se abrió el asistente IA (TapIA)
  | 'assistant_question'     // se envió una pregunta a TapIA
  | 'assistant_answer'       // TapIA respondió (ok=true/false)
  | 'assistant_feedback'     // el usuario calificó una respuesta (value=up/down)
  | 'simulacro_start'        // se abrió el simulacro del reglamento (v6.11.72)
  | 'simulacro_complete'     // se llegó al cierre del simulacro (params: errores)
  // v6.11.171 — foto de los 4 guardada desde la tarjeta del partido. El `stage`
  // (PRE_MATCH | IN_PLAY) es el punto: mide si mover la acción al pie de la
  // tarjeta levanta el 66% de adopción medido antes del cambio, y cuánto aporta
  // la puerta nueva de sacarla con el partido ya empezado.
  | 'match_photo_capture'
  // v6.11.335 — video de la temporada: cuántos lo miran, lo comparten o lo bajan.
  | 'season_video_open'
  | 'season_video_share'
  | 'season_video_download';

interface AppEventParams {
  [key: string]: any;
}

/**
 * Trackea un evento de acción del participante en GA4 + Clarity.
 */
export function trackAppEvent(event: AppEvent, params?: AppEventParams) {
  try {
    window.gtag?.('event', event, {
      event_category: 'app',
      ...params,
    });
    window.clarity?.('set', 'app_event', event);
    ph()?.capture(event, params);
  } catch {
    // Silencioso
  }
}

export interface AppContext {
  category?: string | null;
  teamStatus?: string | null;
  seasonPhase?: 'playoffs' | 'regular' | 'preseason' | string | null;
  isPwa?: boolean;
  // id de perfil (UUID) para identificar la persona en PostHog. NUNCA el DNI.
  userId?: string | null;
}

/**
 * Setea tags de segmentación en Clarity (1 vez por sesión / al cambiar equipo).
 * Permite cortar dead-clicks, recordings y embudos por categoría/fase/PWA.
 */
export function setAppContext(ctx: AppContext) {
  try {
    if (ctx.category) window.clarity?.('set', 'category', ctx.category);
    if (ctx.teamStatus) window.clarity?.('set', 'team_status', ctx.teamStatus);
    if (ctx.seasonPhase) window.clarity?.('set', 'season_phase', ctx.seasonPhase);
    if (typeof ctx.isPwa === 'boolean') window.clarity?.('set', 'is_pwa', String(ctx.isPwa));

    const p = ph();
    if (p) {
      const props: Record<string, any> = {};
      if (ctx.category) props.category = ctx.category;
      if (ctx.teamStatus) props.team_status = ctx.teamStatus;
      if (ctx.seasonPhase) props.season_phase = ctx.seasonPhase;
      if (typeof ctx.isPwa === 'boolean') props.is_pwa = ctx.isPwa;
      // Super properties: se adjuntan a todos los eventos siguientes.
      p.register(props);
      // distinct_id = id de perfil (UUID, no PII).
      if (ctx.userId) p.identify(ctx.userId, props);
    }
  } catch {
    // Silencioso
  }
}

/** Limpia la identidad de PostHog al cerrar sesión (dispositivos compartidos). */
export function resetAppUser() {
  try {
    ph()?.reset();
  } catch {
    // Silencioso
  }
}
